import type { TourPlacement } from './types';

export type DashboardModuleKey =
  | 'coordinator'
  | 'training_manager'
  | 'supervisor'
  | 'instructor';

export interface ModuleDashboardStepSpec {
  stepName: string;
  tourAnchor: string;
  title: string;
  content: string;
  placement?: TourPlacement;
  /** Switch module tab before targeting (panel content or tab highlight). */
  module?: DashboardModuleKey;
  coordinatorTabSlug?: string;
  trainingManagerTabIndex?: number;
  supervisorTabIndex?: number;
  /** Panel targets mount only on their tab; skip element check at filter time. */
  isPanelTarget?: boolean;
}

export function moduleDashboardStepTarget(spec: ModuleDashboardStepSpec): string {
  return `[data-tour="${spec.tourAnchor}"]`;
}

function anchorExists(anchor: string, root: ParentNode): boolean {
  return root.querySelector(`[data-tour="${anchor}"]`) !== null;
}

export function shouldSkipModuleDashboardStep(
  spec: ModuleDashboardStepSpec,
  root: ParentNode = document,
): boolean {
  if (spec.isPanelTarget) {
    return false;
  }
  return !anchorExists(spec.tourAnchor, root);
}

export interface BuiltModuleDashboardStep {
  stepName: string;
  target: string;
  placement: TourPlacement;
  title: string;
  content: string;
  metadata?: Record<string, unknown>;
}

export function buildModuleDashboardTourSteps(
  specs: readonly ModuleDashboardStepSpec[],
  copyByStep: Record<string, { title?: string; content?: string }> = {},
): BuiltModuleDashboardStep[] {
  return specs.map((spec) => {
    const copy = copyByStep[spec.stepName];
    const metadata: Record<string, unknown> = {};
    if (spec.module) metadata.module = spec.module;
    if (spec.coordinatorTabSlug) metadata.coordinatorTabSlug = spec.coordinatorTabSlug;
    if (spec.trainingManagerTabIndex !== undefined) {
      metadata.trainingManagerTabIndex = spec.trainingManagerTabIndex;
    }
    if (spec.supervisorTabIndex !== undefined) {
      metadata.supervisorTabIndex = spec.supervisorTabIndex;
    }

    return {
      stepName: spec.stepName,
      target: moduleDashboardStepTarget(spec),
      placement: spec.placement ?? 'bottom',
      title: copy?.title ?? spec.title,
      content: copy?.content ?? spec.content,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    };
  });
}

export function getModuleDashboardStepSpec(
  specs: readonly ModuleDashboardStepSpec[],
  stepName: string | undefined,
): ModuleDashboardStepSpec | undefined {
  if (!stepName) {
    return undefined;
  }
  return specs.find((spec) => spec.stepName === stepName);
}

const COORDINATOR_TAB_COPY: Array<
  Pick<ModuleDashboardStepSpec, 'stepName' | 'tourAnchor' | 'title' | 'content' | 'coordinatorTabSlug'>
> = [
  {
    stepName: 'coordinator_dashboard_overview',
    tourAnchor: 'coordinator-tab-strip',
    title: 'Coordinator dashboard tabs',
    content:
      'Each tab organizes a part of agency operations. Use Learning Lab practice missions for hands-on workflows.',
  },
  {
    stepName: 'coordinator_payouts_tab',
    tourAnchor: 'coordinator-tab-payouts',
    coordinatorTabSlug: 'payouts',
    title: 'Payouts & Funds',
    content: 'Track Stripe payout readiness and agency fund settings.',
  },
];

export const COORDINATOR_DASHBOARD_STEP_SPECS: readonly ModuleDashboardStepSpec[] = [
  ...COORDINATOR_TAB_COPY.map((tab) => ({
    ...tab,
    module: 'coordinator' as const,
    placement: 'bottom' as const,
  })),
  {
    stepName: 'coordinator_payout_status',
    tourAnchor: 'payout-status',
    title: 'Payout status',
    content: 'Track Stripe payout readiness for your agency.',
    module: 'coordinator',
    coordinatorTabSlug: 'payouts',
    isPanelTarget: true,
    placement: 'top',
  },
];

export const TRAINING_MANAGER_DASHBOARD_STEP_SPECS: readonly ModuleDashboardStepSpec[] = [
  {
    stepName: 'tm_events_tab',
    tourAnchor: 'training-manager-tab-events',
    title: 'My Events',
    content: 'Manage training events for your organization.',
    module: 'training_manager',
    trainingManagerTabIndex: 0,
    placement: 'bottom',
  },
  {
    stepName: 'tm_instructors_tab',
    tourAnchor: 'training-manager-tab-instructors',
    title: 'Instructors',
    content: 'Review and assign instructors to courses.',
    module: 'training_manager',
    trainingManagerTabIndex: 1,
    placement: 'bottom',
  },
  {
    stepName: 'tm_tasks_tab',
    tourAnchor: 'training-manager-tab-tasks',
    title: 'Tasks',
    content: 'Track assignments and follow-ups for your training program.',
    module: 'training_manager',
    trainingManagerTabIndex: 2,
    placement: 'bottom',
  },
  {
    stepName: 'tm_notifications_tab',
    tourAnchor: 'training-manager-tab-notifications',
    title: 'Notifications',
    content: 'Review alerts and updates for your training operations.',
    module: 'training_manager',
    trainingManagerTabIndex: 3,
    placement: 'bottom',
  },
  {
    stepName: 'tm_venues_tab',
    tourAnchor: 'training-manager-tab-venues',
    title: 'Venues',
    content: 'Maintain venue records used by your events.',
    module: 'training_manager',
    trainingManagerTabIndex: 4,
    placement: 'bottom',
  },
];

export const SUPERVISOR_DASHBOARD_STEP_SPECS: readonly ModuleDashboardStepSpec[] = [
  {
    stepName: 'supervisor_activity_tab',
    tourAnchor: 'supervisor-tab-activity',
    title: 'Activity Overview',
    content: 'Review recent team activity notices and priorities.',
    module: 'supervisor',
    supervisorTabIndex: 0,
    placement: 'bottom',
  },
  {
    stepName: 'supervisor_team_tab',
    tourAnchor: 'supervisor-tab-team-members',
    title: 'Team Members',
    content: 'Search, filter, and manage members on your team.',
    module: 'supervisor',
    supervisorTabIndex: 1,
    placement: 'bottom',
  },
  {
    stepName: 'supervisor_add_members_tab',
    tourAnchor: 'supervisor-tab-add-members',
    title: 'Add Members',
    content: 'Invite or add officers to your supervised team.',
    module: 'supervisor',
    supervisorTabIndex: 2,
    placement: 'bottom',
  },
];

export const INSTRUCTOR_DASHBOARD_STEP_SPECS: readonly ModuleDashboardStepSpec[] = [
  {
    stepName: 'instructor_manage_bio',
    tourAnchor: 'instructor-manage-bio',
    title: 'Manage Instructor Bio',
    content: 'Update your public instructor profile and credentials.',
    module: 'instructor',
    placement: 'bottom',
  },
  {
    stepName: 'instructor_stats',
    tourAnchor: 'instructor-stats',
    title: 'Instructor stats',
    content: 'Quick snapshot of your events, students, ratings, and materials.',
    module: 'instructor',
    placement: 'bottom',
  },
  {
    stepName: 'instructor_events',
    tourAnchor: 'instructor-events',
    title: 'Your events',
    content: 'View, manage, and open the events you teach.',
    module: 'instructor',
    placement: 'top',
  },
];